import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/marketing/Nav";

function routeFor(user) {
  if (["agency_owner", "agency_manager", "agency_agent"].includes(user.role)) return "/agency";
  if (user.role === "reviewer") return "/reviewer";
  if (["administrator", "super_administrator"].includes(user.role)) return "/admin";
  return "/app";
}

function GoogleButton() {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={start} data-testid="google-signin-btn" type="button"
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-hborder bg-white px-4 py-3 text-sm font-medium text-navy transition-colors hover:border-navy">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
      Continue with Google
    </button>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          <Logo />
          <h1 className="mt-10 font-serif text-3xl tracking-tight text-heading">{title}</h1>
          <p className="mt-2 text-sm text-muted">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src="https://images.unsplash.com/photo-1593182440709-4b7b56482c55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Aircraft wing above the clouds" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-deepnavy/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-serif text-2xl leading-snug">Know what you need. Know what is complete. Know what needs attention. Know what to do next.</p>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-hborder bg-white px-4 py-3 text-sm text-navy placeholder:text-muted/60 focus:border-tblue";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate(routeFor(res.user));
    else setError(res.error);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to continue preparing your application.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-navy">Email</label>
          <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" autoComplete="email" />
        </div>
        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-navy">Password</label>
          <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password" autoComplete="current-password" />
        </div>
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-4 py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="login-submit">
          {busy ? "Signing in…" : <>Sign in <ArrowRight size={16} /></>}
        </button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted"><span className="h-px flex-1 bg-hborder" />or<span className="h-px flex-1 bg-hborder" /></div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted">No account? <Link to="/register" className="font-semibold text-tblue hover:underline" data-testid="to-register">Create one</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const res = await register(name, email, password);
    setBusy(false);
    if (res.ok) navigate("/app");
    else setError(res.error);
  };

  return (
    <AuthShell title="Create your account" subtitle="Start preparing your visa application in minutes.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-navy">Full name</label>
          <input id="name" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="register-name" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="remail" className="mb-1.5 block text-sm font-medium text-navy">Email</label>
          <input id="remail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="register-email" autoComplete="email" />
        </div>
        <div>
          <label htmlFor="rpassword" className="mb-1.5 block text-sm font-medium text-navy">Password</label>
          <input id="rpassword" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="register-password" autoComplete="new-password" />
          <p className="mt-1 text-xs text-muted">At least 8 characters.</p>
        </div>
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-brandyellow px-4 py-3 text-sm font-semibold text-heading disabled:opacity-60" data-testid="register-submit">
          {busy ? "Creating…" : <>Create account <ArrowRight size={16} /></>}
        </button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted"><span className="h-px flex-1 bg-hborder" />or<span className="h-px flex-1 bg-hborder" /></div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted">Already have an account? <Link to="/login" className="font-semibold text-tblue hover:underline" data-testid="to-login">Sign in</Link></p>
    </AuthShell>
  );
}
